import { ROUTE_BUILDER } from '@/constants/pages';
import { classnames } from '@/helpers';
import { slug } from '@/helpers/urlTs';
import { _t } from '@/locale';
import { SearchPlace } from '@/types/api/services/search';
import { PlacePreview } from '@/types/state/shared';
import MPMapServiceCard from '../components/MPServiceCard/MPMapServiceCard';
import { MPMapServiceCardProps } from '../components/MPServiceCard/types';

import { mapCroLabelProps } from '../helpers';
import { SerpCardItem } from '../tracking';
import { DealSearchItem, SistaminutenSearchItem } from '../types/dealSearch';

export type DealType = 'deals' | 'sistaminuten' | 'brand_deals';

export const mapDealItemToSearchPlace = (place: PlacePreview): SearchPlace => ({
  id: place.id,
  name: place.name,
  slug: place.slug,
  timezone: 'Europe/Stockholm',
  settings: { displayReviews: false, giftcard: false, klarna: false },
  about: {
    name: place.name,
    settings: {
      hasKlarna: place.settings?.klarna ?? false,
      hasQliro: place.settings?.qliro ?? false,
      sellsGiftCard: place.settings?.giftcard ?? false,
      wellness: place.settings?.wellness ?? false,
      displayCertificates: place.settings?.displayCertificates ?? false,
      hasCampaigns: place.settings?.campaigns ?? false,
    },
  },
  contact: {
    address: { street: place.address?.street, city: place.address?.city },
    position: place.position,
  },
  position: place.position,
  images: place.profileImageURL ? [place.profileImageURL] : undefined,
});

export function buildMapPlaces(type: DealType, items: DealSearchItem[] | SistaminutenSearchItem[]): SearchPlace[] {
  if (type === 'deals' || type === 'brand_deals') {
    return (items as DealSearchItem[])
      .filter((item) => item.place.position)
      .map((item) => mapDealItemToSearchPlace(item.place));
  }
  return (items as SistaminutenSearchItem[])
    .filter((item) => item.position)
    .map((item) => mapDealItemToSearchPlace(item));
}

export const searchResultsContainerClassName = classnames(
  'gap-lg grid grid-flow-row-dense grid-cols-[repeat(auto-fill,minmax(312px,1fr))]',
);

export function renderMapItem(
  type: DealType,
  items: DealSearchItem[] | SistaminutenSearchItem[],
  asPopupItem: boolean,
  onItemClick?: (item: SerpCardItem) => void,
) {
  return (place: SearchPlace) => {
    // override styles when in popup since maptiler applies some default styles to the content container we need to reset
    const mapItemClassName = asPopupItem ? classnames('px-0 py-0 drop-shadow-none border-none') : undefined;

    if (type === 'deals' || type === 'brand_deals') {
      const item = (items as DealSearchItem[]).find((i) => i.place.id === place.id);

      if (!item) return null;

      const cardProps: MPMapServiceCardProps = {
        type: 'deal',
        title: item.name,
        subtitle: item.place.name,
        reviewRating: item.place.rating?.score,
        ratingCount: item.place.rating?.count,
        price: item.priceLabel,
        originalPrice: item.originalPriceLabel,
        duration: item.durationLabel,
        to: ROUTE_BUILDER.dealDetails(`${slug(item.name)}-${item.id}`),
        onClick: () => onItemClick?.({ type: 'deal', data: item }),
        labels: (item.discountLabels || []).map((discount) => ({ label: discount.label, variant: 'discount' })),
        croLabel: mapCroLabelProps(item.croLabels?.[0]),
      };

      return <MPMapServiceCard {...cardProps} className={mapItemClassName} />;
    }

    if (type === 'sistaminuten') {
      const item = (items as SistaminutenSearchItem[]).find((i) => i.id === place.id);

      if (!item) return null;

      const cardProps: MPMapServiceCardProps = {
        type: 'sistaminuten',
        title: item.name,
        subtitle: [item.address?.street, item.address?.city].filter(Boolean).join(', '),
        reviewRating: item.rating?.score,
        ratingCount: item.rating?.count,
        matchedServicesCount: item.matchedServices?.length ?? 0,
        bookableServicesCountLabel: item.bookableServicesCountLabel,
        merchantLogoUrl: item.profileImageURL,
        croLabel: mapCroLabelProps({ type: 'smt' }),
        labels: [{ label: _t('UpToDiscountPercentage', { discount: item.lastMinuteDiscount }), variant: 'discount' }],
        to: `${ROUTE_BUILDER.placePage(`${item.slug}-${item.id}`)}?sistaminuten=1`,
        onClick: () => onItemClick?.({ type: 'sistaminuten', data: item }),
      };

      return <MPMapServiceCard {...cardProps} className={mapItemClassName} />;
    }
  };
}
